/**
 * 管理员页面处理函数 Hook
 * 包含所有业务逻辑处理函数
 */
import { useCallback } from "react";
import { Modal, message } from "antd";
import { AdminRecord } from "../config/panelTableConfig";
import { PermissionService } from "../services/permissionService";

export interface UseAdminHandlersProps {
  // 数据操作
  createAdmin: (values: any) => Promise<boolean>;
  updateAdmin: (id: string, values: any) => Promise<boolean>;
  updateAdminStatus: (id: string, status: number) => Promise<boolean>;
  resetPassword: (id: string) => Promise<boolean>;
  deleteAdmin: (record: AdminRecord) => Promise<boolean>;
  batchDeleteAdmins: (ids: string[]) => Promise<boolean>;
  assignRolesToAdmin: (adminId: string, roleIds: string[]) => Promise<boolean>;
  fetchPermissions: (adminId: string) => Promise<any>;

  // 弹窗控制
  closeCreateModal: () => void;
  closeEditModal: () => void;
  closeAssignRoleModal: () => void;
  openPermissionModal: (record: AdminRecord) => void;
  openAssignRoleModal: (record: AdminRecord) => void;
  openEditModal: (record: AdminRecord) => void;

  // 数据刷新
  handleSearch: () => Promise<void>;
  loadAdminStats: () => void;
  updatePermissionCount: (adminId: string, count: number) => void;

  // 当前记录
  currentRecord: AdminRecord | null;

  // 选中项
  selectedRowKeys: string[];
  setSelectedRowKeys: (keys: string[]) => void;
}

export const useAdminHandlers = ({
  createAdmin,
  updateAdmin,
  updateAdminStatus,
  resetPassword,
  deleteAdmin,
  batchDeleteAdmins,
  assignRolesToAdmin,
  fetchPermissions,
  closeCreateModal,
  closeEditModal,
  closeAssignRoleModal,
  openPermissionModal,
  openAssignRoleModal,
  openEditModal,
  handleSearch,
  loadAdminStats,
  updatePermissionCount,
  currentRecord,
  selectedRowKeys,
  setSelectedRowKeys,
}: UseAdminHandlersProps) => {
  // 创建管理员处理
  const handleCreate = useCallback(
    async (values: any) => {
      const success = await createAdmin(values);
      if (success) {
        closeCreateModal();
        await handleSearch();
        loadAdminStats();
      }
      return success;
    },
    [createAdmin, closeCreateModal, handleSearch, loadAdminStats]
  );

  // 编辑管理员处理
  const handleEdit = useCallback(
    async (values: any) => {
      if (!currentRecord) return false;
      const success = await updateAdmin(currentRecord.id, values);
      if (success) {
        closeEditModal();
        await handleSearch();
        loadAdminStats();
      }
      return success;
    },
    [currentRecord, updateAdmin, closeEditModal, handleSearch, loadAdminStats]
  );

  // 切换管理员状态
  const handleToggleStatus = useCallback(
    async (record: AdminRecord) => {
      const newStatus = record.account_status === 1 ? 2 : 1;
      const success = await updateAdminStatus(record.id, newStatus);
      if (success) {
        await handleSearch();
        loadAdminStats();
      }
    },
    [updateAdminStatus, handleSearch, loadAdminStats]
  );

  // 删除管理员
  const handleDelete = useCallback(
    async (record: AdminRecord) => {
      Modal.confirm({
        title: "确认删除",
        content: `确定要删除管理员 "${record.admin_account}" 吗？此操作不可恢复！`,
        okText: "确认删除",
        cancelText: "取消",
        okType: "danger",
        onOk: async () => {
          try {
            const success = await deleteAdmin(record);
            if (success) {
              message.success("管理员删除成功");
              await handleSearch();
              loadAdminStats();
            } else {
              message.error("管理员删除失败");
            }
          } catch (error) {
            message.error("管理员删除失败");
          }
        },
      });
    },
    [deleteAdmin, handleSearch, loadAdminStats]
  );

  // 重置密码
  const handleResetPassword = useCallback(
    async (record: AdminRecord) => {
      Modal.confirm({
        title: "确认重置密码",
        content: `确定要重置管理员 "${record.admin_account}" 的密码吗？`,
        okText: "确认",
        cancelText: "取消",
        onOk: async () => {
          try {
            const success = await resetPassword(record.id);
            if (success) {
              message.success("密码重置成功");
              await handleSearch();
            } else {
              message.error("密码重置失败");
            }
          } catch (error) {
            message.error("密码重置失败");
          }
        },
      });
    },
    [resetPassword, handleSearch]
  );

  // 分配角色
  const handleAssignRole = useCallback(
    async (record: AdminRecord) => {
      openAssignRoleModal(record);
    },
    [openAssignRoleModal]
  );

  // 角色保存
  const handleRoleSave = useCallback(
    async (selectedRoleIds: string[]) => {
      if (!currentRecord?.id) return false;
      const success = await assignRolesToAdmin(currentRecord.id, selectedRoleIds);
      if (success) {
        closeAssignRoleModal();
        await handleSearch();
        loadAdminStats();
        // 重新加载当前管理员的权限数量
        const newCount = await PermissionService.getAdminPermissionCount(currentRecord.id);
        updatePermissionCount(currentRecord.id, newCount);
      }
      return success;
    },
    [currentRecord, assignRolesToAdmin, closeAssignRoleModal, handleSearch, loadAdminStats, updatePermissionCount]
  );

  // 查看权限
  const handleViewPermissions = useCallback(
    async (record: AdminRecord) => {
      try {
        // 先获取权限数据
        await fetchPermissions(record.id);
        // 权限数据获取完成后再打开弹窗
        openPermissionModal(record);
      } catch (error) {
        message.error("获取权限数据失败");
      }
    },
    [fetchPermissions, openPermissionModal]
  );

  // 批量删除处理
  const handleBatchDelete = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      message.warning("请先选择要删除的管理员");
      return;
    }

    Modal.confirm({
      title: "确认批量删除",
      content: `确定要删除选中的 ${selectedRowKeys.length} 个管理员吗？此操作不可恢复。`,
      okText: "确认",
      cancelText: "取消",
      okType: "danger",
      onOk: async () => {
        try {
          const success = await batchDeleteAdmins(selectedRowKeys);
          if (success) {
            message.success(`成功删除 ${selectedRowKeys.length} 个管理员`);
            setSelectedRowKeys([]); // 清空选中项
            await handleSearch();
            loadAdminStats();
          } else {
            message.error("批量删除失败");
          }
        } catch (error) {
          message.error("批量删除失败");
        }
      },
    });
  }, [selectedRowKeys, batchDeleteAdmins, setSelectedRowKeys, handleSearch, loadAdminStats]);

  return {
    handleCreate,
    handleEdit,
    handleToggleStatus,
    handleDelete,
    handleResetPassword,
    handleAssignRole,
    handleRoleSave,
    handleViewPermissions,
    handleBatchDelete,
  };
};

